/**
 * Product Dependencies Configuration
 *
 * Defines which products require other products to be included.
 * When a product is selected, its dependencies are auto-included.
 * Dependencies cannot be removed while dependent products are selected.
 */

export const dependencies = {
  // ═══════════════════════════════════════════════════════════════════════════
  // USER LICENSE DEPENDENCIES
  // Admin, ReadOnly, Mobile all require Excel Connect (free)
  // ═══════════════════════════════════════════════════════════════════════════
  users: {
    'BE:USER_ADMIN_DEV': ['BE:USER_XLC'],      // Admin → Excel Connect
    'BE:USER_READ_ACCESS': ['BE:USER_XLC'],    // ReadOnly → Excel Connect
    'BE:USER_MOBILE': ['BE:USER_XLC'],         // Mobile → Excel Connect
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // GL CONNECT DEPENDENCIES
  // ═══════════════════════════════════════════════════════════════════════════
  glConnect: {
    'MOD:68': ['PIPE:15', 'CONN:34'],  // GL Connect Cloud → Pipeline + Connector
    'MOD:67': ['CONN:22'],              // GL Connect EBS → EBS Connector
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CLOUD REPORTS (SplashOC) - All require Pipeline + Connector
  // Pipeline (PIPE:15) requires Connector (CONN:34)
  // ═══════════════════════════════════════════════════════════════════════════
  reportsCloud: {
    // HCM
    'MOD:40': ['PIPE:15', 'CONN:34'],   // Human Resources
    'MOD:41': ['PIPE:15', 'CONN:34'],   // Payroll - US
    'MOD:128': ['PIPE:15', 'CONN:34'],  // Payroll - GB
    'MOD:123': ['PIPE:15', 'CONN:34'],  // Recruit Cloud
    'MOD:42': ['PIPE:15', 'CONN:34'],   // Benefits
    'MOD:62': ['PIPE:15', 'CONN:34'],   // Time & Labor
    'MOD:61': ['PIPE:15', 'CONN:34'],   // Absences
    'MOD:60': ['PIPE:15', 'CONN:34'],   // Performance Mgmt
    'MOD:124': ['PIPE:15', 'CONN:34'],  // Learning Management
    'MOD:63': ['PIPE:15', 'CONN:34'],   // Taleo EE Recruit
    // Finance
    'MOD:44': ['PIPE:15', 'CONN:34'],   // General Ledger
    'MOD:45': ['PIPE:15', 'CONN:34'],   // Accounts Payables
    'MOD:46': ['PIPE:15', 'CONN:34'],   // Accounts Receivables
    'MOD:47': ['PIPE:15', 'CONN:34'],   // Cash Management
    'MOD:48': ['PIPE:15', 'CONN:34'],   // Fixed Assets
    'MOD:49': ['PIPE:15', 'CONN:34'],   // Expenses
    'MOD:129': ['PIPE:15', 'CONN:34'],  // Grants Management
    'MOD:64': ['PIPE:15', 'CONN:34'],   // Projects
    'MOD:76': ['PIPE:15', 'CONN:34'],   // Cloud Audit
    // SCM
    'MOD:50': ['PIPE:15', 'CONN:34'],   // Inventory
    'MOD:51': ['PIPE:15', 'CONN:34'],   // Order Management
    'MOD:52': ['PIPE:15', 'CONN:34'],   // Purchasing
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // EBS REPORTS - All require EBS Connector
  // ═══════════════════════════════════════════════════════════════════════════
  reportsEBS: {
    // HCM
    'MOD:29': ['CONN:22'],   // Human Resources
    'MOD:1': ['CONN:22'],    // Payroll - US
    'MOD:70': ['CONN:22'],   // Payroll - CA
    'MOD:71': ['CONN:22'],   // Payroll - GB
    'MOD:72': ['CONN:22'],   // Payroll - IE
    'MOD:73': ['CONN:22'],   // Payroll - INTL
    'MOD:74': ['CONN:22'],   // Payroll - Mexico
    'MOD:69': ['CONN:22'],   // Payroll - Saudi
    'MOD:25': ['CONN:22'],   // Affordable Care Act
    'MOD:10': ['CONN:22'],   // Compensation Workbench
    'MOD:30': ['CONN:22'],   // Benefits
    'MOD:32': ['CONN:22'],   // iRecruitment
    'MOD:24': ['CONN:22'],   // Learning Management
    'MOD:31': ['CONN:22'],   // Time & Labor
    'MOD:23': ['CONN:22'],   // Performance Management
    // Finance
    'MOD:26': ['CONN:22'],   // General Ledger
    'MOD:2': ['CONN:22'],    // Payables
    'MOD:3': ['CONN:22'],    // Receivables
    'MOD:7': ['CONN:22'],    // Cash Management
    'MOD:4': ['CONN:22'],    // Fixed Assets
    'MOD:20': ['CONN:22'],   // iExpenses
    'MOD:28': ['CONN:22'],   // Subledger Accounting
    'MOD:8': ['CONN:22'],    // Grants Management
    'MOD:6': ['CONN:22'],    // Project Accounting
    'MOD:75': ['CONN:22'],   // European Localizations
    // SCM
    'MOD:15': ['CONN:22'],   // Advanced Supply Chain Planning
    'MOD:9': ['CONN:22'],    // Inventory
    'MOD:5': ['CONN:22'],    // Purchasing
    'MOD:27': ['CONN:22'],   // Order Management
    // Manufacturing
    'MOD:11': ['CONN:22'],   // Cost Management
    'MOD:14': ['CONN:22'],   // Process Manufacturing
    'MOD:12': ['CONN:22'],   // Bills of Material
    'MOD:13': ['CONN:22'],   // Work In Process
    'MOD:18': ['CONN:22'],   // Quality
    'MOD:19': ['CONN:22'],   // Material Resource Planning
    // Other
    'MOD:21': ['CONN:22'],   // Enterprise Asset Management
    'MOD:22': ['CONN:22'],   // Service Contracts
    'MOD:17': ['CONN:22'],   // Sales (CRM)
    'MOD:16': ['CONN:22'],   // Incentive Compensation (CRM)
  },
};

/**
 * Get all dependencies for a given SKU
 * @param {string} sku - The product SKU
 * @returns {string[]} Array of required dependency SKUs
 */
export const getDependencies = (sku) => {
  // Check all dependency categories
  for (const category of Object.values(dependencies)) {
    if (category[sku]) {
      return category[sku];
    }
  }
  return [];
};

/**
 * Get all SKUs that depend on a given SKU
 * @param {string} dependencySku - The dependency SKU to check
 * @returns {string[]} Array of SKUs that require this dependency
 */
export const getDependents = (dependencySku) => {
  const dependents = [];
  for (const category of Object.values(dependencies)) {
    for (const [sku, deps] of Object.entries(category)) {
      if (deps.includes(dependencySku)) {
        dependents.push(sku);
      }
    }
  }
  return dependents;
};

/**
 * Check if a dependency can be removed (no active dependents)
 * @param {string} dependencySku - The SKU to check
 * @param {string[]} selectedSkus - Currently selected SKUs
 * @returns {boolean} True if can be removed
 */
export const canRemoveDependency = (dependencySku, selectedSkus) => {
  const dependents = getDependents(dependencySku);
  // Can remove if no selected products depend on it
  return !dependents.some(sku => selectedSkus.includes(sku));
};

/**
 * Get all dependencies that should be auto-added for selected products
 * @param {string[]} selectedSkus - Currently selected SKUs
 * @returns {string[]} Array of dependency SKUs to auto-add
 */
export const getRequiredDependencies = (selectedSkus) => {
  const required = new Set();
  for (const sku of selectedSkus) {
    const deps = getDependencies(sku);
    deps.forEach(dep => required.add(dep));
  }
  return Array.from(required);
};

/**
 * Dependency metadata for display
 */
export const dependencyInfo = {
  'CONN:22': {
    name: 'Oracle EBS Connector',
    price: 25000,
    reason: 'Required for EBS Reports'
  },
  'CONN:34': {
    name: 'Oracle Fusion Cloud (Real-Time)',
    price: 25000,
    reason: 'Required for Oracle Cloud connectivity'
  },
  'PIPE:15': {
    name: 'Oracle Fusion Cloud (Data Pipeline)',
    price: 25000,
    reason: 'Required for Oracle Cloud Reports'
  },
  'BE:USER_XLC': {
    name: 'Excel Connect',
    price: 0,
    reason: 'Required for user licenses'
  },
};

export default dependencies;
